'use strict';

import { DEFAULT_PER_PAGE_ELEMENTS } from '@core/constants/DEFAULT';
import { EXTRA_TIME_CHECK_VALID_ACCESS_TOKEN } from '@core/constants/OAUTH';
import { SEARCH_IMG_QUERY_PARAMS } from '@core/constants/API';
import { SEARCHER_TYPES } from '@core/constants/SEARCHER';
import { REPORT_TYPE } from '@core/constants/REPORTS';
import {
    TRIGGER_SEARCH_QUERY_EVENT,
    UPDATE_LANGUAGE_EVENT,
} from '@core/constants/EVENTS';
import { SUB_STRING_CODE } from '@core/constants/CONSTANTS';
import {
    checkDarkMode,
    checkDefaultView,
    showLoadingSpinner,
    checkShadowDOMSupport,
} from '@core/utils/utils';
import { checkAuthorize } from '@components/modals/LoginModal/utils';
import { STORE_SEARCHER_FILTERS } from '@core/constants/STORE';
import { getCartList } from '@core/utils/getCartState';
import { getStoreConfiguration } from '@core/utils/store';
import { addLoginSuccessEvent, checkValidAccessToken } from '@core/utils/oauth';
import { getCurrentSearcherObj } from '@core/utils/searcher';
import { redirectOldRoutes } from '@core/utils/navigate';

import { updateTitle, redirectTo, loadPreviousReports, searchReports } from './utils';
import { checkUserStatus } from '@core/utils/wesog-user';

/**
 * @type {object} - The query params to search the images in the api
 */
let searchImgQuery = {
    [SEARCH_IMG_QUERY_PARAMS.FILTERS]: [],
    [SEARCH_IMG_QUERY_PARAMS.AI]: false,
    [SEARCH_IMG_QUERY_PARAMS.PAGE_AI]: 0,
    [SEARCH_IMG_QUERY_PARAMS.LIMIT_AI]: DEFAULT_PER_PAGE_ELEMENTS,
    [SEARCH_IMG_QUERY_PARAMS.FIRST_QUERY]: true,
};

/**
 * Get the first query to the api to show the results.
 * @returns {Promise<void>}
 */
async function firstQuery() {
    const /** @type {URLSearchParams} */ urlParams = new URLSearchParams(
            window.location.search,
        );

    const /** @type {Object|undefined} */ currentSearcherObj = getCurrentSearcherObj();

    searchImgQuery[SEARCH_IMG_QUERY_PARAMS.FILTERS] = [
        `${SUB_STRING_CODE.CAT_ED_REP}:${
            urlParams.get(SEARCHER_TYPES.REPORT_TYPE) || REPORT_TYPE.ED_MOST_VIEWED
        }`,
    ];

    if (currentSearcherObj) {
        /* add in the searchImgQuery the new filters from the currentSearcherObj and avoid duplicates */
        searchImgQuery[SEARCH_IMG_QUERY_PARAMS.FILTERS] = [
            ...new Set([
                ...searchImgQuery[SEARCH_IMG_QUERY_PARAMS.FILTERS],
                ...currentSearcherObj[STORE_SEARCHER_FILTERS.ACTIVATED_FILTERS],
            ]),
        ];

        searchImgQuery[SEARCH_IMG_QUERY_PARAMS.PAGE_AI] =
            currentSearcherObj[STORE_SEARCHER_FILTERS.PAGE_VALUE] || 0;
    }

    showLoadingSpinner();
    await searchReports({ searchImgQuery });
}

// Check ShadowDOM support first, before any web components are loaded
checkShadowDOMSupport();

// First, check if we need to redirect from old routes
redirectOldRoutes();

redirectTo();
checkDefaultView();
checkDarkMode();
checkAuthorize();

document.addEventListener('DOMContentLoaded', async () => {
    await checkUserStatus();
    await addLoginSuccessEvent();

    const /** @type {Object} */ configuration = getStoreConfiguration();
    searchImgQuery[SEARCH_IMG_QUERY_PARAMS.LIMIT_AI] =
        configuration?.perPage || DEFAULT_PER_PAGE_ELEMENTS;

    await checkValidAccessToken({ extraTime: EXTRA_TIME_CHECK_VALID_ACCESS_TOKEN });
    await firstQuery();
    await getCartList();
    searchImgQuery[SEARCH_IMG_QUERY_PARAMS.FIRST_QUERY] = false;
});

document.addEventListener(TRIGGER_SEARCH_QUERY_EVENT, (event) => {
    searchImgQuery = {
        ...searchImgQuery,
        ...event.detail,
    };

    if (event.cancelable) return;

    showLoadingSpinner();
    if (searchImgQuery[SEARCH_IMG_QUERY_PARAMS.LOAD_PREVIOUS]) {
        loadPreviousReports({ searchImgQuery });
    } else {
        searchReports({ searchImgQuery });
    }
});

document.addEventListener(UPDATE_LANGUAGE_EVENT, () => {
    updateTitle();
});
